/**
 * ClientContactsSection - Manages client contacts within Client Edit screen
 * MVP-Critical: Required for Quotes, Estimates, Invoices, and email delivery
 * EF-CLIENT-ONBOARDING-ADMIN-CONTROLS-V1: Added portal access controls
 */
import { useState, useEffect, useCallback } from "react";
// EVENTSFLO-EMAIL-SAFETY-PREVIEW-RECIPIENT-REDIRECT-AND-AUDIT-V1
import { confirmRealRecipientSend } from "../lib/realEmailGuard";
import { 
  Users, Plus, Pencil, Trash2, Star, Mail, Phone, 
  FileText, Receipt, File, Loader2, X, Check, AlertCircle,
  Send, Key, RefreshCw, ExternalLink, Clock, CheckCircle2, XCircle
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { toast } from "sonner";
import {
  getClientContacts,
  createClientContact,
  updateClientContact,
  deleteClientContact,
  sendClientOnboardingLink,
  getClientUsers,
  clientPasswordResetRequest,
} from "../lib/api";

const EMPTY_CONTACT = {
  first_name: "",
  last_name: "",
  role: "",
  email: "",
  mobile: "",
  is_primary: false,
  in_estimate: false,
  in_quote: false,
  in_invoice: false,
  description: "",
};

// Status badge component
const PortalStatusBadge = ({ status, onboardingCompleted }) => {
  if (!status) return null;
  
  const configs = {
    invited: {
      variant: "outline",
      className: "border-amber-500/50 text-amber-400 bg-amber-500/10",
      icon: Clock,
      label: "Invited"
    },
    active: {
      variant: "outline",
      className: "border-green-500/50 text-green-400 bg-green-500/10",
      icon: CheckCircle2,
      label: "Active"
    },
    inactive: {
      variant: "outline",
      className: "border-gray-500/50 text-gray-400 bg-gray-500/10",
      icon: XCircle,
      label: "Inactive"
    },
    pending: {
      variant: "outline",
      className: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      icon: Clock,
      label: "Pending"
    }
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  
  return (
    <Badge variant={config.variant} className={`text-xs py-0 h-5 ${config.className}`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
      {status === "active" && onboardingCompleted && (
        <span className="ml-1 text-green-300">✓</span>
      )}
    </Badge>
  );
};

export function ClientContactsSection({ clientId, readOnly = false }) {
  const [contacts, setContacts] = useState([]);
  const [clientUsers, setClientUsers] = useState({});  // Map of contact_id -> client_user
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [formData, setFormData] = useState(EMPTY_CONTACT);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [actionInProgress, setActionInProgress] = useState(null); // contactId being acted upon

  // Load contacts and client users
  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getClientContacts(clientId, { active_only: true });
      setContacts(res.data || []);
      
      // Also load client users to know their status
      try {
        const usersRes = await getClientUsers(clientId);
        const usersMap = {};
        (usersRes.data || usersRes || []).forEach(user => {
          if (user.contact_id) {
            usersMap[user.contact_id] = user;
          }
        });
        setClientUsers(usersMap);
      } catch (userErr) {
        console.warn("[CLIENT ADMIN ACTION] Failed to load client users:", userErr);
        // Non-critical - continue without user status
      }
    } catch (err) {
      console.error("[CLIENT ADMIN ACTION] Failed to load contacts:", err);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      loadContacts();
    }
  }, [clientId, loadContacts]);

  // Open dialog for new contact
  const handleAddContact = () => {
    setEditingContact(null);
    setFormData({ ...EMPTY_CONTACT });
    setErrors({});
    setDialogOpen(true);
  };

  // Open dialog for editing contact
  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setFormData({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      role: contact.role || "",
      email: contact.email || "",
      mobile: contact.mobile || "",
      is_primary: contact.is_primary || false,
      in_estimate: contact.in_estimate || false,
      in_quote: contact.in_quote || false,
      in_invoice: contact.in_invoice || false,
      description: contact.description || "",
    });
    setErrors({});
    setDialogOpen(true);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = "First name is required";
    if (!formData.last_name.trim()) newErrors.last_name = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save contact (create or update)
  const handleSaveContact = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (editingContact) {
        // Update existing
        await updateClientContact(editingContact.id, formData);
        toast.success("Contact updated");
      } else {
        // Create new
        await createClientContact(clientId, {
          ...formData,
          client_id: clientId,
        });
        toast.success("Contact added");
      }
      setDialogOpen(false);
      loadContacts();
    } catch (err) {
      console.error("[CLIENT ADMIN ACTION] Failed to save contact:", err);
      toast.error(err.response?.data?.detail || "Failed to save contact");
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm delete
  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  // Perform soft delete
  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    try {
      await deleteClientContact(contactToDelete.id, false);
      toast.success("Contact deactivated");
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      loadContacts();
    } catch (err) {
      console.error("[CLIENT ADMIN ACTION] Failed to delete contact:", err);
      toast.error("Failed to delete contact");
    }
  };

  // Toggle primary status
  const handleTogglePrimary = async (contact) => {
    try {
      await updateClientContact(contact.id, { is_primary: !contact.is_primary });
      toast.success(contact.is_primary ? "Primary status removed" : "Set as primary contact");
      loadContacts();
    } catch (err) {
      toast.error("Failed to update contact");
    }
  };

  // === PORTAL ACCESS ACTIONS ===
  
  // Send or Resend Onboarding Link
  const handleSendAccessLink = async (contact) => {
    const contactId = contact.id;
    const existingUser = clientUsers[contactId];
    const isResend = !!existingUser;
    
    console.log(`[CLIENT ADMIN ACTION] action: ${isResend ? 'resend_link' : 'send_link'}, contact: ${contactId}`);

    // EVENTSFLO-EMAIL-SAFETY-PREVIEW-RECIPIENT-REDIRECT-AND-AUDIT-V1
    // Typed-CONFIRM gate for non-TEST recipients. Backend still
    // redirects in preview; this is defence in depth.
    const _displayName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
      || contact.full_name || contact.name || "";
    if (!confirmRealRecipientSend({ name: _displayName, email: contact.email })) {
      toast.info("Access link send cancelled.");
      return;
    }

    setActionInProgress(contactId);
    try {
      const response = await sendClientOnboardingLink(contactId);
      const data = response.data || response;
      
      if (data.success) {
        if (data.email_sent) {
          toast.success(
            isResend ? "Access link resent successfully" : "Access link sent successfully",
            { description: `Email sent to ${contact.email}` }
          );
        } else {
          // Email was not actually sent (e.g., no email provider)
          toast.warning("Link generated but email delivery failed", {
            description: data._dev_link ? "Check console for link" : "Contact support"
          });
          if (data._dev_link) {
            console.log("[CLIENT ADMIN ACTION] Dev link:", data._dev_link);
          }
        }
        console.log(`[CLIENT ADMIN ACTION] action: ${isResend ? 'resend_link' : 'send_link'}, result: success`);
        loadContacts(); // Refresh to show updated status
      } else {
        toast.error(data.message || "Failed to send access link");
        console.log(`[CLIENT ADMIN ACTION] action: ${isResend ? 'resend_link' : 'send_link'}, result: failed`);
      }
    } catch (err) {
      console.error("[CLIENT ADMIN ACTION] Send access link error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Failed to send access link";
      toast.error(errorMsg);
      console.log(`[CLIENT ADMIN ACTION] action: ${isResend ? 'resend_link' : 'send_link'}, result: failed, error: ${errorMsg}`);
    } finally {
      setActionInProgress(null);
    }
  };

  // Reset Password
  const handleResetPassword = async (contact) => {
    const contactId = contact.id;
    const email = contact.email;
    
    if (!email) {
      toast.error("Contact has no email address");
      return;
    }
    
    console.log(`[CLIENT ADMIN ACTION] action: reset_password, contact: ${contactId}`);
    
    setActionInProgress(contactId);
    try {
      const response = await clientPasswordResetRequest(email);
      const data = response.data || response;
      
      if (data.success) {
        toast.success("Password reset email sent", {
          description: `Reset link sent to ${email}`
        });
        console.log(`[CLIENT ADMIN ACTION] action: reset_password, result: success`);
      } else {
        // This shouldn't happen with our endpoint design, but handle it
        toast.info(data.message || "Password reset request processed");
        console.log(`[CLIENT ADMIN ACTION] action: reset_password, result: processed`);
      }
    } catch (err) {
      console.error("[CLIENT ADMIN ACTION] Reset password error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Failed to send reset email";
      toast.error(errorMsg);
      console.log(`[CLIENT ADMIN ACTION] action: reset_password, result: failed, error: ${errorMsg}`);
    } finally {
      setActionInProgress(null);
    }
  };

  // Get user status for a contact
  const getContactPortalStatus = (contactId) => {
    return clientUsers[contactId] || null;
  };

  return (
    <Card className="bg-card border-white/5" data-testid="client-contacts-section">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Users className="h-4 w-4" />
          Client Contacts ({contacts.length})
        </CardTitle>
        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddContact}
            data-testid="add-contact-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No contacts added</p>
            {!readOnly && (
              <p className="text-xs mt-1">Add contacts for quotes, estimates, and invoices</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((contact) => {
              const portalUser = getContactPortalStatus(contact.id);
              const hasPortalAccess = !!portalUser;
              const portalStatus = portalUser?.status;
              const onboardingCompleted = portalUser?.onboarding_completed;
              const isActionLoading = actionInProgress === contact.id;
              
              return (
                <div
                  key={contact.id}
                  className="flex items-center justify-between p-3 bg-surface-elevated/20 rounded hover:bg-surface-elevated/30 transition-colors group"
                  data-testid={`contact-item-${contact.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {contact.first_name} {contact.last_name}
                      </span>
                      {contact.is_primary && (
                        <Badge variant="default" className="bg-amber-600 hover:bg-amber-600 text-xs">
                          <Star className="h-3 w-3 mr-1" />
                          Primary
                        </Badge>
                      )}
                      {contact.role && (
                        <span className="text-xs text-muted-foreground">({contact.role})</span>
                      )}
                      {/* Portal Status Badge */}
                      {hasPortalAccess && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <PortalStatusBadge 
                                  status={portalStatus} 
                                  onboardingCompleted={onboardingCompleted}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">
                                Portal Status: {portalStatus}
                                {onboardingCompleted && " (Setup Complete)"}
                                {portalUser?.last_login && (
                                  <><br/>Last login: {new Date(portalUser.last_login).toLocaleDateString()}</>
                                )}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {contact.email}
                      </span>
                      {contact.mobile && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {contact.mobile}
                        </span>
                      )}
                    </div>
                    {/* Document flags */}
                    <div className="flex items-center gap-2 mt-2">
                      {contact.in_estimate && (
                        <Badge variant="outline" className="text-xs py-0 h-5">
                          <FileText className="h-3 w-3 mr-1" />
                          Estimate
                        </Badge>
                      )}
                      {contact.in_quote && (
                        <Badge variant="outline" className="text-xs py-0 h-5">
                          <File className="h-3 w-3 mr-1" />
                          Quote
                        </Badge>
                      )}
                      {contact.in_invoice && (
                        <Badge variant="outline" className="text-xs py-0 h-5">
                          <Receipt className="h-3 w-3 mr-1" />
                          Invoice
                        </Badge>
                      )}
                    </div>
                  </div>

                  {!readOnly && (
                    <div className="flex items-center gap-1">
                      {/* Portal Access Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            disabled={isActionLoading}
                            data-testid={`portal-actions-${contact.id}`}
                          >
                            {isActionLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <ExternalLink className="h-4 w-4 mr-1" />
                                Portal
                              </>
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {!hasPortalAccess ? (
                            <DropdownMenuItem 
                              onClick={() => handleSendAccessLink(contact)}
                              data-testid={`send-access-link-${contact.id}`}
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Send Access Link
                            </DropdownMenuItem>
                          ) : (
                            <>
                              <DropdownMenuItem 
                                onClick={() => handleSendAccessLink(contact)}
                                data-testid={`resend-access-link-${contact.id}`}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Resend Access Link
                              </DropdownMenuItem>
                              {onboardingCompleted && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleResetPassword(contact)}
                                    data-testid={`reset-password-${contact.id}`}
                                  >
                                    <Key className="h-4 w-4 mr-2" />
                                    Reset Password
                                  </DropdownMenuItem>
                                </>
                              )}
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            Status: {hasPortalAccess ? (
                              <span className={
                                portalStatus === 'active' ? 'text-green-400' : 
                                portalStatus === 'invited' ? 'text-amber-400' : 'text-gray-400'
                              }>
                                {portalStatus || 'Unknown'}
                              </span>
                            ) : (
                              <span className="text-gray-500">No portal access</span>
                            )}
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {/* Existing Edit/Delete buttons (show on hover) */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleTogglePrimary(contact)}
                          title={contact.is_primary ? "Remove primary" : "Set as primary"}
                          data-testid={`toggle-primary-${contact.id}`}
                        >
                          <Star className={`h-4 w-4 ${contact.is_primary ? "fill-amber-500 text-amber-500" : ""}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditContact(contact)}
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          onClick={() => handleDeleteClick(contact)}
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? "Edit Contact" : "Add Contact"}
            </DialogTitle>
            <DialogDescription>
              {editingContact
                ? "Update contact details"
                : "Add a new contact for this client"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  className={errors.first_name ? "border-red-500" : ""}
                  data-testid="contact-first-name"
                />
                {errors.first_name && (
                  <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Smith"
                  className={errors.last_name ? "border-red-500" : ""}
                  data-testid="contact-last-name"
                />
                {errors.last_name && (
                  <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>
                )}
              </div>
            </div>

            {/* Role */}
            <div>
              <Label className="text-xs">Role</Label>
              <Input
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder="e.g. Finance Director, Operations Manager"
                data-testid="contact-role"
              />
            </div>

            {/* Email */}
            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@company.com"
                className={errors.email ? "border-red-500" : ""}
                data-testid="contact-email"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Mobile */}
            <div>
              <Label className="text-xs">Mobile</Label>
              <Input
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="+44 7700 900123"
                data-testid="contact-mobile"
              />
            </div>

            {/* Checkboxes */}
            <div className="space-y-3 pt-2 border-t border-white/10">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_primary"
                  checked={formData.is_primary}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_primary: checked })}
                  data-testid="contact-is-primary"
                />
                <label htmlFor="is_primary" className="text-sm flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" />
                  Primary Contact
                </label>
              </div>

              <p className="text-xs text-muted-foreground mt-3 mb-2">Include in document emails:</p>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="in_estimate"
                  checked={formData.in_estimate}
                  onCheckedChange={(checked) => setFormData({ ...formData, in_estimate: checked })}
                  data-testid="contact-in-estimate"
                />
                <label htmlFor="in_estimate" className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Estimates
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="in_quote"
                  checked={formData.in_quote}
                  onCheckedChange={(checked) => setFormData({ ...formData, in_quote: checked })}
                  data-testid="contact-in-quote"
                />
                <label htmlFor="in_quote" className="text-sm flex items-center gap-2">
                  <File className="h-4 w-4" />
                  Quotes
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="in_invoice"
                  checked={formData.in_invoice}
                  onCheckedChange={(checked) => setFormData({ ...formData, in_invoice: checked })}
                  data-testid="contact-in-invoice"
                />
                <label htmlFor="in_invoice" className="text-sm flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Invoices
                </label>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional notes about this contact..."
                rows={2}
                data-testid="contact-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSaveContact} disabled={submitting} data-testid="save-contact-btn">
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : editingContact ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {editingContact ? "Save Changes" : "Add Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate{" "}
              <strong>
                {contactToDelete?.first_name} {contactToDelete?.last_name}
              </strong>
              . They will no longer receive documents or emails. This action can be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-contact"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default ClientContactsSection;
